import { IdentificationIcon } from '@heroicons/react/24/outline';
import { SubPageHeader } from '@/components/shared';
import { useAuth } from '@/contexts/AuthContext';
import { useCollaboratorSelfQuery } from '@/features/Collaborators/Data/hooks/useCollaboratorSelfQuery';
import type { DataUpdateRequest, DpRequest } from '../../../../../shared/dpRequests';
import { DataUpdateForm } from '../../components/DataUpdateForm';
import { PendingDataUpdatePanel } from '../../components/PendingDataUpdatePanel';
import { useMyDpRequestsQuery } from '../../hooks/data/useDpRequestQueries';
import { selfToFormValues } from '../../utils/selfToFormValues';
import { DP_REQUESTS_PATH } from '../../routes';

function findOpenDataUpdate(requests: DpRequest[] | undefined): DataUpdateRequest | null {
    const open = (requests ?? []).find(
        (request): request is DataUpdateRequest =>
            request.type === 'data_update' && (request.status === 'pending_dp' || request.status === 'pending_correction')
    );
    return open ?? null;
}

export function DataUpdatePage() {
    const { user } = useAuth();
    const email = user?.email ?? null;
    const collaboratorId = user?.collaboratorId ?? '';
    const selfQuery = useCollaboratorSelfQuery(collaboratorId);
    const mineQuery = useMyDpRequestsQuery(email);
    const open = findOpenDataUpdate(mineQuery.data);

    const header = (
        <SubPageHeader
            backPlacement="end"
            backTo={DP_REQUESTS_PATH}
            icon={<IdentificationIcon className="h-6 w-6" aria-hidden="true" />}
            title="Atualização de dados"
            description="Peça a alteração do seu cadastro. O DP revisa antes de aplicar."
        />
    );

    if (selfQuery.isLoading || mineQuery.isLoading) {
        return (
            <div className="space-y-6">
                {header}
                <div className="h-64 animate-pulse rounded-2xl bg-raro-gray-100" />
            </div>
        );
    }

    if (!collaboratorId || !selfQuery.data) {
        return (
            <div className="space-y-6">
                {header}
                <p className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-800">
                    Não encontramos seu cadastro de colaborador. Procure o DP.
                </p>
            </div>
        );
    }

    if (open?.status === 'pending_dp') {
        return (
            <div className="space-y-6">
                {header}
                <PendingDataUpdatePanel request={open} />
            </div>
        );
    }

    const correction = open?.status === 'pending_correction' ? open : null;

    return (
        <div className="space-y-6">
            {header}
            <DataUpdateForm
                key={correction?.id ?? 'new'}
                profile={{ collaboratorId, values: selfToFormValues(selfQuery.data as unknown as Record<string, unknown>) }}
                mode={correction ? 'correction' : 'create'}
                initialGroups={correction?.groups ?? []}
                initialAttachments={correction?.attachments ?? []}
                requestId={correction?.id}
                correctionNote={correction?.decision?.note ?? null}
            />
        </div>
    );
}

export default DataUpdatePage;
